import { useState } from 'react';
import { useScrollReveal, animStyle } from '../../../hooks/useScrollReveal';

interface FaqItem {
  q: string;
  a: string;
  tag: string;
}

const faqs: FaqItem[] = [
  {
    tag: '相談について',
    q: '初めての相談だけでも対応していただけますか？',
    a: 'もちろんです。「まだ具体的な依頼ではないが話を聞きたい」「費用感だけ知りたい」といったご相談も大歓迎です。どんな些細なことでもお気軽にご連絡ください。',
  },
  {
    tag: '料金・契約',
    q: '料金体系はどのようになっていますか？',
    a: 'ご依頼内容・規模・運用するSNSの種類・投稿頻度などによって異なります。まずはヒアリングのうえ、貴社に最適なプランをご提案させていただきます。まずはお気軽にお問い合わせください。',
  },
  {
    tag: '対応SNS',
    q: '対応可能なSNSプラットフォームを教えてください。',
    a: 'Facebook・X（Twitter）・Instagram・YouTube・TikTok・LINE公式アカウントに対応しています。また、各プラットフォームの広告運用も一括してお任せいただけます。新しいプラットフォームへの対応についてもご相談ください。',
  },
  {
    tag: '初期対応',
    q: 'SNSアカウントがない状態からでも対応できますか？',
    a: 'はい、アカウントの開設・プロフィール設定・初期コンテンツ制作から対応可能です。ゼロからのスタートでも、貴社のブランドに合ったSNS戦略を一緒に構築します。',
  },
  {
    tag: '契約期間',
    q: '契約の最低期間はありますか？',
    a: 'SNS運用は継続的な施策が成果につながるため、基本的には3ヶ月以上のご契約をお願いしています。ただし、スポット対応（イベント・キャンペーンのみ等）もご相談によっては対応可能です。まずはご相談ください。',
  },
  {
    tag: '引き継ぎ・体制',
    q: '社内担当者が変わっても継続してもらえますか？',
    a: '弊社ではチーム体制でご支援しているため、担当者が変わっても円滑に引き継げる仕組みを整えています。また、貴社の担当者向けに引き継ぎ資料・運用マニュアルの整備もサポート可能です。',
  },
  {
    tag: '業種・規模',
    q: 'どのような業種・規模の企業に対応していますか？',
    a: 'IT・エンタメ・自動車・音楽・航空・製造・消費財など幅広い業種のクライアントをご支援してきました。スタートアップから大手企業まで、規模を問わずご相談いただけます。',
  },
  {
    tag: 'レポート・報告',
    q: '運用状況のレポートはどのように共有されますか？',
    a: '月次でパフォーマンスレポートを提出します。フォロワー数の推移・エンゲージメント率・リーチ数・広告効果などを分かりやすくまとめ、改善提案とともにご報告します。',
  },
];

function FaqAccordionItem({
  item,
  isOpen,
  onToggle,
  visible,
  delay,
}: {
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
  visible: boolean;
  delay: number;
}) {
  return (
    <div
      className="border-b border-white/10 last:border-b-0"
      style={animStyle(visible, 'up', delay, 650)}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-4 py-6 text-left group cursor-pointer"
      >
        <span className="text-white/30 text-xs font-['Montserrat'] tracking-widest pt-0.5 shrink-0 w-20 hidden sm:block group-hover:text-white/50 transition-colors">
          {item.tag}
        </span>
        <div className="flex-1">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="text-white/40 text-sm font-['Montserrat'] font-light shrink-0 mt-0.5">Q.</span>
              <p className="text-white/85 text-sm leading-relaxed group-hover:text-white transition-colors duration-200">
                {item.q}
              </p>
            </div>
            <div className="w-6 h-6 flex items-center justify-center shrink-0 border border-white/15 group-hover:border-white/35 transition-colors duration-200 mt-0.5">
              <i
                className={`ri-${isOpen ? 'subtract' : 'add'}-line text-xs text-white/50 group-hover:text-white/80 transition-all duration-300`}
              />
            </div>
          </div>
        </div>
      </button>

      {/* Answer panel */}
      <div
        style={{
          maxHeight: isOpen ? '300px' : '0px',
          overflow: 'hidden',
          transition: 'max-height 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div className="pb-6 flex items-start gap-4">
          <span className="hidden sm:block w-20 shrink-0" />
          <div className="flex-1 flex items-start gap-3">
            <span className="text-white/40 text-sm font-['Montserrat'] font-light shrink-0 mt-0.5">A.</span>
            <p className="text-white/55 text-sm leading-loose">{item.a}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Faq() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const { ref: headerRef, visible: headerVisible } = useScrollReveal(0.3);
  const { ref: listRef, visible: listVisible } = useScrollReveal(0.05);

  const toggle = (idx: number) => setOpenIdx(openIdx === idx ? null : idx);

  return (
    <section id="faq" className="w-full bg-[#151515] py-14 md:py-24 px-5 sm:px-8 md:px-20">
      {/* Header */}
      <div ref={headerRef} className="mb-10 md:mb-14">
        <div
          style={{
            width: headerVisible ? '32px' : '0px',
            height: '2px',
            backgroundColor: '#fff',
            marginBottom: '24px',
            transition: 'width 0.7s ease-out',
          }}
        />
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h2
              className="text-5xl md:text-6xl font-bold text-white tracking-widest font-['Montserrat']"
              style={animStyle(headerVisible, 'fromLeft', 150, 750)}
            >
              FAQ
            </h2>
            <p
              className="text-white/50 text-sm mt-3 tracking-wider"
              style={animStyle(headerVisible, 'fromLeft', 280, 700)}
            >
              よくあるご質問
            </p>
          </div>
          <p
            className="text-white/35 text-xs leading-relaxed max-w-xs"
            style={animStyle(headerVisible, 'fromRight', 200, 700)}
          >
            解決しない場合はお気軽に<br />
            <button
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="underline text-white/55 hover:text-white transition-colors cursor-pointer"
            >
              お問い合わせフォーム
            </button>
            よりご連絡ください。
          </p>
        </div>
      </div>

      {/* FAQ list */}
      <div ref={listRef} className="max-w-4xl">
        {faqs.map((item, idx) => (
          <FaqAccordionItem
            key={idx}
            item={item}
            isOpen={openIdx === idx}
            onToggle={() => toggle(idx)}
            visible={listVisible}
            delay={idx * 80}
          />
        ))}
      </div>

      {/* CTA strip */}
      <div
        className="mt-12 md:mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 border border-white/10 px-5 sm:px-8 py-5 sm:py-6"
        style={animStyle(listVisible, 'up', 700, 700)}
      >
        <div>
          <p className="text-white/80 text-sm font-medium tracking-wider mb-1">
            まだ疑問が解決しませんか？
          </p>
          <p className="text-white/40 text-xs">
            担当者が丁寧にご回答いたします。お気軽にご連絡ください。
          </p>
        </div>
        <button
          onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          className="flex items-center gap-3 bg-white text-black text-xs tracking-widest px-7 sm:px-8 py-3 hover:bg-white/85 transition-colors cursor-pointer whitespace-nowrap shrink-0 w-full sm:w-auto justify-center"
        >
          お問い合わせ
          <i className="ri-arrow-right-line" />
        </button>
      </div>
    </section>
  );
}
